import type { RouteLocationNormalized } from 'vue-router';

import mixpanel from 'mixpanel-browser';

import { useAnalyticsApi } from '@/composables/api/analytics/useAnalyticsApi';
import type { TourRecommendationCode } from '@/composables/api/usageAnalysis/types';
import { useUserProfile } from '@/composables/useUserProfile';
import constants from '@/config/constants';
// @TODO REMOVE
// @ts-ignore
import { useInternationalization } from '@/i18n';
import type { ParcelShopTrackingSource } from '@/maps/components/parcelShops/constants';
import router from '@/router';
import useAuthStore from '@/stores/auth';
import type { EventProperties, RouteData } from '@/types/analytics';
import { ThemeName } from '@/types/theme';
import { getBrowserProperties } from '@/utils/browserInfo';
import { isProduction } from '@/utils/environment';

import '@/types/analytics';

/**
 * Determine if user has consented to analytics tracking.
 *
 * Strategy (checked in order):
 * 1. CookieHub consent (if available - used by production and E2E tests)
 * 2. Default deny when consent cannot be determined (prevents analytics pollution / ensures compliance)
 */
const hasUserConsented = (): boolean => {
  // First check if CookieHub exists and hasConsented is callable (production, stage, or E2E tests with mock)
  if (window.cookiehub && typeof window.cookiehub.hasConsented === 'function') {
    return Boolean(window.cookiehub.hasConsented('analytics'));
  }

  // Default: deny tracking (shouldn't reach here on production where CookieHub exists)
  return false;
};

const isUserEmailAllowed = (): boolean => {
  const { profile } = useUserProfile();
  if (!profile.value?.email) return true;
  const userEmailDomain = profile.value.email?.split('@')[1];
  return (
    !userEmailDomain ||
    !constants.internalEmails.includes(userEmailDomain) ||
    !isProduction
  );
};

const getRouteData = (
  route: RouteLocationNormalized | undefined
): RouteData => {
  if (!route) return { routeName: '', context: '' };
  const { name, params, matched } = route;

  return {
    routeName: (name as string) || '',
    ...params,
    context:
      (matched?.[0]?.name as string) || matched?.[0]?.path?.split('/')[1] || ''
  };
};

const trackEvent = (eventName: string, properties?: EventProperties): void => {
  const authStore = useAuthStore();

  // Check if user can be tracked
  if (!hasUserConsented() || !isUserEmailAllowed()) return;

  const { currentLanguage } = useInternationalization();
  const { trackAnalyticsEvent } = useAnalyticsApi();

  const { profile } = useUserProfile();
  const userId = profile.value?.id || null;

  const eventData: EventProperties = {
    ...properties,
    ...getRouteData(router?.currentRoute?.value),
    roles: authStore.userRoles,
    selectedLanguage: currentLanguage.value || '',
    appVersion: import.meta.env.VITE_VERSION,
    // Browser/device properties (Mixpanel equivalents)
    ...getBrowserProperties(userId)
  };

  trackAnalyticsEvent(eventName, eventData);
};

// Identify user in Mixpanel for session recording association only
const identifyUser = (userId: string): void => {
  mixpanel.identify(userId);
};

// Mixpanel truncates list properties past 255 entries.
const maxTrackedSelectValues = 50;

// Event tracking functions
const trackingEvents = {
  viewPage: (page: string, from: string, other?: EventProperties): void => {
    trackEvent(`View ${page}`, { from, ...other });
  },
  user: (
    userId: string,
    tenants: string[],
    language?: string,
    utm?: EventProperties,
    theme?: ThemeName
  ): void => trackEvent('User', { userId, tenants, language, theme, ...utm }),
  identify: identifyUser,
  login: (): void => trackEvent('Login'),
  logout: (): void => trackEvent('Logout'),
  clickQSTab: (tab: string, dashboard: string): void =>
    trackEvent('Click QS Tab', { tab, dashboard }),
  changeAddressCorrectionTab: (tab: string): void =>
    trackEvent('Change Address Correction Tab', { tab }),
  rejectAddressCorrection: (): void => trackEvent('Reject Address Correction'),
  approveAddressCorrection: (): void =>
    trackEvent('Approve Address Correction'),
  changeSuco: (): void => trackEvent('Change Suco'),
  multiActivateDrivers: (type: string): void =>
    trackEvent('Multi Activate Drivers', { type }),
  multiDeleteDrivers: (type: string): void =>
    trackEvent('Multi Delete Drivers', { type }),
  openLSMapMarker: (tourPercentage: number, tourStatus: string): void =>
    trackEvent('Open Live Status Map Marker', {
      tourPercentage,
      tourStatus
    }),
  addSubcontractor: (): void => trackEvent('Add Subcontractor'),
  deleteSubcontractor: (): void => trackEvent('Delete Subcontractor'),
  updateSubcontractor: (): void => trackEvent('Update Subcontractor'),
  openTDV: (source: string): void => trackEvent('Open TDV', { source }),
  downloadTableData: (tableId: string, format: string): void =>
    trackEvent(`Download Table ${tableId}`, { format }),
  activateDriver: (): void => trackEvent('Activate Driver'),
  deactivateDriver: (): void => trackEvent('Deactivate Driver'),
  deleteDriver: (): void => trackEvent('Delete Driver'),
  editDriver: (): void => trackEvent('Edit Driver'),
  openTourHistory: (source: string): void =>
    trackEvent('Open Tour History', { source }),
  rotateLSScreen: (orientation: string): void =>
    trackEvent('Rotate Live Status Screen', { orientation }),
  clickAddressErrors: (): void => trackEvent('Click Address Errors'),
  clickConflictsTDV: (): void => trackEvent('Click Conflicts'),
  clickHealthStatusDepot: (health: string): void =>
    trackEvent('Click Health Status Depot', { health }),
  editUser: (): void => trackEvent('Edit User'),
  deleteUser: (): void => trackEvent('Delete User'),
  addUser: (): void => trackEvent('Add User'),
  editPermissions: (): void => trackEvent('Edit Permission'),
  openHelpCenter: (): void => trackEvent('Open Help Center'),
  clickPerformanceDetails: (): void => trackEvent('Click Performance Details'),
  openSelectInput: (identifier: string): void =>
    trackEvent('Open Select Input', { identifier }),
  filterSelectInput: (identifier: string, values: unknown[]): void =>
    trackEvent('Filter Select Input', {
      identifier,
      values: values.slice(0, maxTrackedSelectValues),
      count: values.length
    }),
  viewDeviceInfo: (page: string): void =>
    trackEvent('View Device Info', { page }),
  viewTimeFrameDetails: (): void => trackEvent('View Time Frame Details'),
  clickDrivenHistoryControls: (action: string): void =>
    trackEvent('Click Driven History Controls', { action }),
  openDrivenHistorySequence: (source: string): void =>
    trackEvent('Open Driven History Sequence', { source }),
  clickJumpToMapLiveStatus: (): void =>
    trackEvent('Click Jump To Map Live Status'),
  clickJumpToTableLiveStatus: (): void =>
    trackEvent('Click Jump To Table Live Status'),
  openTDHTourInfo: (): void => trackEvent('Open Tour Driven History Info'),
  openTDHTourLegend: (): void => trackEvent('Open Tour Driven History Legend'),
  sortingChanged: (column: unknown): void =>
    trackEvent('Sorting Changed', { column }),
  clickCheckRecommendationButton: (nrOfTours: number): void =>
    trackEvent('Check Recommendation Button Clicked', { nrOfTours }),
  hoverTourRecommendationIcon: (recommendation: TourRecommendationCode): void =>
    trackEvent('Hover Tour Recommendation Icon', { recommendation }),
  goToBetterPlanner: (source: string): void =>
    trackEvent('Go to Better Planner', { source }),
  clickWidget: (widgetName: string): void =>
    trackEvent(`Click "${widgetName}" Widget`),
  clickTodoWidgetItem: (todoItem: string): void =>
    trackEvent(`Click "${todoItem}" in Todos Widget`),
  clickCompareTDHTab: (): void => trackEvent('Click Compare TDH Tab'),
  clickInvestigateTDHTab: (): void => trackEvent('Click Investigate TDH Tab'),
  clickWPListItem: (source: string): void =>
    trackEvent(`Click Waypoint List Item`, { source }),
  clickTDHMarker: (source: string): void =>
    trackEvent('Click TDH Marker', { source }),
  clickOpenWPJobDetails: (source: string): void =>
    trackEvent('Click Open WP Job Details', { source }),
  depotInsightsFeedback: (positive: boolean): void =>
    trackEvent('Depot Insights Feedback', { positive }),
  depotInsightsComment: (comment: string): void =>
    trackEvent('Depot Insights Comment', {
      comment: comment || 'empty'
    }),
  depotInsightsWidgetClicked: (): void =>
    trackEvent('Depot Insights Widget Clicked'),
  keyStatisticsInsightsViewMoreClicked: (): void =>
    trackEvent('Key Statistics View More Clicked'),
  keyStatisticsInsightsFeedback: (positive: boolean): void =>
    trackEvent('Key Statistics Insights Feedback', { positive }),
  keyStatisticsInsightsComment: (comment: string): void =>
    trackEvent('Key Statistics Insights Comment', {
      comment: comment || 'empty'
    }),
  clickParcelShopMarker: (source: ParcelShopTrackingSource): void =>
    trackEvent('Click Parcel Shop Marker', { source }),
  clickParcelShopOpeningHours: (source: ParcelShopTrackingSource): void =>
    trackEvent('Click Parcel Shop Opening Hours', { source }),
  clickEtaAtaGraphWaypoint: (): void =>
    trackEvent('Click ETA/ATA Graph Waypoint'),
  toggleDarkMode: (isDarkMode: boolean): void =>
    trackEvent('Click Dark Mode Toggle', {
      theme: isDarkMode ? ThemeName.dark : ThemeName.light
    }),
  // Live issue monitoring (SESC-13337). `count` and `source` on the write
  // actions are what tell bulk handling apart from one-at-a-time. Nothing here
  // carries a parcel id, name, address or search term — see the searchable
  // fields in LiveStatusIssuesDatatable for why that matters.
  clickLiveIssuesTab: (tab: string): void =>
    trackEvent('Click Live Issues Tab', { tab }),
  openLiveIssueParcel: (source: string): void =>
    trackEvent('Open Live Issue Parcel', { source }),
  markLiveIssuesAsHandled: (count: number, source: string): void =>
    trackEvent('Mark Live Issue As Handled', { count, source }),
  reopenLiveIssues: (count: number, source: string): void =>
    trackEvent('Reopen Live Issue', { count, source }),
  reportLiveIssuesAsFalse: (
    count: number,
    source: string,
    // The reason *code*, never the user's free text.
    reason: string
  ): void =>
    trackEvent('Report Live Issue As False', { count, source, reason }),
  filterLiveIssues: (filter: string, count: number): void =>
    trackEvent('Filter Live Issues', { filter, count }),
  searchLiveIssues: (hasResults: boolean): void =>
    trackEvent('Search Live Issues', { hasResults })
};

export default trackingEvents;
